import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
    VideoCameraOutlined,
} from '@ant-design/icons';
import SnapshotsContainer from "./SnapshotsContainer";
import ProfileContainer from "./ProfileContainer";
import VideoPlayer from "./VideoPlayer";
import Tab from "src/components/Common/Tab";
import PdfReportApi from "src/services/apis/pdfReport";
import { useIsMobileDevice } from "src/utils/hooks";
import { MEDIA_PROCTORING_MODAL_TABS } from "src/constants/globalConstants";
import "./VideoPlayer.scss"

const VideoProctoringContainer = ({
    videoSessionsList,
    isParentScreenProctoring = false,
    fetchSessionSnapshots,
    isAiProctored,
    referenceImageLink,
    audioProctoring = false,
}) => {
    const [sessionVideoUrls, setSessionVideoUrls] = useState([])
    const [currentVideoIndex, setCurrentVideoIndex] = useState(0)
    const [loading, setLoading] = useState(false)
    const [isPlaying, setIsPlaying] = useState(false)
    const [seekValue, setSeekValue] = useState(0)
    const [isSessionVideoEnded, setIsSessionVideoEnded] = useState(false)
    const [canPlayVideoLoading, setCanPlayVideoLoading] = useState(false)
    const [currentSession, setCurrentSession] = useState("")
    const [currentTab, setCurrentTab] = useState("")
    const [sessionSnapshots, setSessionSnapshots] = useState([])
    const [sessionCriticalSnapshots, setSessionCriticalSnapshots] = useState([])
    const [sessionProfile, setSessionProfile] = useState({})
    const [audioEvents, setAudioEvents] = useState([])
    const isMobile = useIsMobileDevice()
    const { linkId } = useParams()

    const [sessionsList, setSessionsList] = useState([])

    useEffect(() => {
        if (videoSessionsList?.length > 0) {
            setSessionsList(videoSessionsList)
        }
    }, [videoSessionsList])

    // VAD speech events are correlated to USER (camera) video only, so they are
    // fetched once per assessment for the user-proctoring player, not for screen.
    // Gated on audioProctoring — no audio_events exist unless the position config
    // enabled it, so skip the call entirely when it's off.
    useEffect(() => {
        if (isParentScreenProctoring || !linkId || !audioProctoring) return
        (async () => {
            try {
                const response = await PdfReportApi.getAudioEvents(linkId)
                if (response?.success) {
                    setAudioEvents(response.events || [])
                }
            } catch (err) { }
        })()
    }, [linkId, isParentScreenProctoring, audioProctoring])

    useEffect(() => {
        if (sessionsList?.length > 0 && !currentSession) {
            setCurrentSession(sessionsList[0])
            setCurrentTab(MEDIA_PROCTORING_MODAL_TABS.SNAPSHOTS)
        }
    }, [sessionsList, isParentScreenProctoring, currentSession])

    useEffect(() => {
        (async () => {
            if (currentSession?.id) {
                if (currentTab === MEDIA_PROCTORING_MODAL_TABS.SNAPSHOTS && (isParentScreenProctoring ? currentSession?.isAIProctored : currentSession?.isProctored)) {
                    getSessionSnapshots(currentSession?.id)
                } else if (currentTab === MEDIA_PROCTORING_MODAL_TABS.AV_RECORDING) {
                    resetVideoPlayer()
                    fetchSessionVideos(currentSession?.id)
                } else if (currentTab === MEDIA_PROCTORING_MODAL_TABS.PROFILE && !isParentScreenProctoring) {
                    fetchSessionProfile(currentSession?.id)
                }
            }
        })()
    }, [currentTab, currentSession, isParentScreenProctoring])

    const resetVideoPlayer = () => {
        setSeekValue(0)
        setCurrentVideoIndex(0)
        setIsPlaying(false)
        setIsSessionVideoEnded(false)
        setCanPlayVideoLoading(false)
    }

    const fetchSessionVideos = async (sessionId) => {
        try {
            setLoading(true)
            const response = await PdfReportApi.getSessionVideos(sessionId)
            if (response.success) {
                setSessionVideoUrls(response.data)
            }
        } catch (err) { } finally {
            setLoading(false)
        }
    }

    const getSessionSnapshots = async (sessionId) => {
        try {
            setLoading(true)
            const snapshots = await fetchSessionSnapshots(sessionId)
            setSessionSnapshots(snapshots)
            setSessionCriticalSnapshots(snapshots.filter(snap => snap?.critical))
        } catch (err) {
        } finally {
            setLoading(false)
        }
    }

    const fetchSessionProfile = async (sessionId) => {
        try {
            setLoading(true)
            const response = await PdfReportApi.getSessionProfile(sessionId)
            if (response.success) {
                setSessionProfile(response.data)
            }
        } catch (err) {
            setSessionProfile(null)
        } finally {
            setLoading(false)
        }
    }

    return (
        <div className={`h-full mt-8 flex flex-col items-center`}>
            <div className="w-full flex justify-start mt-4 mb-12 scroll-x" style={{ minHeight: '40px' }}>
                {sessionsList?.length > 0 && (
                    <Tab
                        currentKey={currentSession?.id}
                        data={sessionsList.map((session, index) => {
                            return {
                                name: `Session ${index + 1}`,
                                key: session?.id,
                            }
                        })}
                        onClick={(sessionId) => {
                            const session = sessionsList?.find(session => session?.id === sessionId)
                            setCurrentSession(session)
                        }}
                        titleClassName={"text-16"}
                    />
                )}
            </div>
            {
                <div className={`mt-4 mb-12 ${isMobile ? "flex-col" : ""} flex justify-center  w-full`}>
                    <div className={`${isMobile ? "flex flex-col" : "relative right-[-80]"}`}>
                        {
                            Object.values(MEDIA_PROCTORING_MODAL_TABS).slice(0, 2)
                                .filter(tab => isParentScreenProctoring ? tab !== "Profile" : !!tab)
                                .map(mediaTab =>
                                    <button
                                        key={mediaTab}
                                        className={`filter-btn b-20 mr-5 ${currentTab === mediaTab ? 'selected-filter-btn text-white' : ''} txt-14 weight-600 ${isMobile ? "mt-4 mb-4" : ""}`}
                                        onClick={() => setCurrentTab(mediaTab)}
                                    >
                                        {mediaTab}
                                    </button>
                                )
                        }
                    </div>
                    <button
                        className={`filter-btn b-20 ${currentTab === MEDIA_PROCTORING_MODAL_TABS.AV_RECORDING ? 'selected-filter-btn text-white' : 'entire-video-btn-shadow'} txt-14 weight-600 ${isMobile ? "mt-4" : "entire-video-btn"}`}
                        onClick={() => setCurrentTab(MEDIA_PROCTORING_MODAL_TABS.AV_RECORDING)}
                    >
                        <VideoCameraOutlined className="mr-8" />
                        See Entire Video
                    </button>
                </div>
            }
            <div className="h-full flex flex-col items-center">
                {
                    currentTab === MEDIA_PROCTORING_MODAL_TABS.SNAPSHOTS
                        ? (isParentScreenProctoring ? currentSession?.isAIProctored : currentSession?.isProctored)
                            ? <SnapshotsContainer
                                isParentSnapshotsModal={false}
                                allSnapshots={sessionSnapshots}
                                criticalSnapshots={sessionCriticalSnapshots}
                                isAiProctored={isAiProctored}
                                referenceImageLink={referenceImageLink}
                            />
                            : <MediaNotProctored
                                isMobile={isMobile}
                                message={"This session is getting proctored right now, please check after a while."}
                            />
                        : !isParentScreenProctoring && currentTab === MEDIA_PROCTORING_MODAL_TABS.PROFILE
                            ? sessionProfile === null
                                ? <MediaNotProctored
                                    isMobile={isMobile}
                                    message={"Profile photo was not available for proctoring."}
                                />
                                : <ProfileContainer
                                    sessionProfile={sessionProfile}
                                    loading={loading}
                                />
                            : <VideoPlayer
                                loading={loading}
                                setLoading={setLoading}
                                sessionVideoUrls={sessionVideoUrls}
                                currentVideoIndex={currentVideoIndex}
                                setCurrentVideoIndex={setCurrentVideoIndex}
                                seekValue={seekValue}
                                setSeekValue={setSeekValue}
                                isPlaying={isPlaying}
                                setIsPlaying={setIsPlaying}
                                isSessionVideoEnded={isSessionVideoEnded}
                                setIsSessionVideoEnded={setIsSessionVideoEnded}
                                canPlayVideoLoading={canPlayVideoLoading}
                                setCanPlayVideoLoading={setCanPlayVideoLoading}
                                audioEvents={audioEvents}
                            />
                }
            </div>
        </div>
    )
}

export default VideoProctoringContainer

const MediaNotProctored = ({ isMobile, message = "" }) => <div style={{ height: "400px" }} className={`flex items-center justify-center ${!isMobile ? "text-24 weight-600" : "text-16 weight-500"}`}>
    {message}
</div>
